import React, { useState, useRef, useEffect } from 'react';
import { apiPost } from '../api/client';
import type { EmailFamilyRequest, EmailFamilyResponse } from '../types/api';
import { useToast } from './ToastContext';
import './EmailFamilyModal.css';

interface Props {
  vaultId: string;
  vaultName: string;
  onClose: () => void;
}

const SUBJECT_MAX = 100;
const MESSAGE_MAX = 2000;

export default function EmailFamilyModal({ vaultId, vaultName, onClose }: Props) {
  const { addToast } = useToast();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const subjectRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    subjectRef.current?.focus();
  }, []);

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();

    const trimmedSubject = subject.trim();
    const trimmedMessage = message.trim();

    if (!trimmedSubject || !trimmedMessage) return;

    setSending(true);
    try {
      const result = await apiPost<EmailFamilyResponse>(
        `/api/vault/${vaultId}/email-family`,
        { subject: trimmedSubject, message: trimmedMessage } satisfies EmailFamilyRequest
      );

      setSent(true);
      addToast(
        result.sent > 0 ? 'success' : 'info',
        result.sent === 0
          ? 'No other family members to email.'
          : `Email sent to ${result.sent} family member${result.sent !== 1 ? 's' : ''}.`
      );

      // Auto-close after a brief moment
      setTimeout(onClose, 1200);
    } catch {
      addToast('error', 'Failed to send email. Please try again.');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="email-family-overlay" role="dialog" aria-modal="true" aria-label="Email Family">
      <div className="email-family-panel">

        {/* Header */}
        <div className="email-family-header">
          <div className="email-family-header-info">
            <svg className="email-family-header-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="2" y="4" width="20" height="16" rx="2"/>
                <path d="M2 7l10 7 10-7"/>
              </svg>
            <div>
              <span className="email-family-header-title">Email Family</span>
              <span className="email-family-header-sub">
                Sends to all family members in {vaultName}
              </span>
            </div>
          </div>
          <button
            className="email-family-close"
            onClick={onClose}
            aria-label="Close"
            disabled={sending}
          >
            ✕
          </button>
        </div>

        {/* Form */}
        <form className="email-family-form" onSubmit={handleSend}>

          <div className="email-family-field">
            <label className="email-family-label" htmlFor="ef-subject">Subject</label>
            <input
              id="ef-subject"
              ref={subjectRef}
              className="form-input email-family-input"
              type="text"
              placeholder="e.g. Family gathering this Saturday"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              maxLength={SUBJECT_MAX}
              required
              disabled={sending || sent}
            />
            <span className="email-family-count">
              {subject.length}/{SUBJECT_MAX}
            </span>
          </div>

          <div className="email-family-field">
            <label className="email-family-label" htmlFor="ef-message">Message</label>
            <textarea
              id="ef-message"
              className="form-input email-family-textarea"
              placeholder="Write your message here…"
              value={message}
              onChange={e => setMessage(e.target.value)}
              maxLength={MESSAGE_MAX}
              rows={6}
              required
              disabled={sending || sent}
            />
            <span className="email-family-count">
              {message.length}/{MESSAGE_MAX}
            </span>
          </div>

          <p className="email-family-reply-note">
            💬 All family members appear in the To: field — Reply All to keep the whole family in the conversation.
          </p>

          <div className="email-family-actions">
            <button
              type="button"
              className="btn-ghost"
              onClick={onClose}
              disabled={sending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={sending || sent || !subject.trim() || !message.trim()}
            >
              {sending ? 'Sending…' : sent ? 'Sent ✓' : 'Send Email'}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}
